import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ILabReportListState } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import { useLabReportStatsListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatsListAPI";
import { useLabReportFilterListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportFilterListAPI";

import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useNavigate } from "react-router";
import { Truck } from "lucide-react";
import clsx from "clsx";
import { useLabReportExcemptionAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportExcemptionAPI";

export interface ISampleRequest {
  exception_reason: string;
}

interface IUploadLabReportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  requisitionId: string | null;
  isDayClosing?: boolean;
  testDetails?: any;
  invoiceId?: string;
}

const LabExemptionModal: React.FC<IUploadLabReportModalProps> = ({
  open,
  setOpen,
  requisitionId,
  isDayClosing,
  testDetails,
  invoiceId,
}) => {
  const cancelButtonRef = useRef(null);
  const { getRouteKey } = useRouter();
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { labReportListFilter } = useRouteInfo(getRouteKey("LAB_REPORT", "id")).routeState.state as ILabReportListState;
  const { execute: markExcmption } = useLabReportExcemptionAPI();

  const fetchStatsList = useLabReportStatsListAPI();
  const { execute: fetchLabReports } = useLabReportFilterListAPI();
  const [loader, setLoader] = useState(false);

  const isLabTestUrl = location.pathname.includes("lab-test");

  const formHook = useForm<ISampleRequest>({
    mode: "onChange",
    defaultValues: {
      exception_reason: "",
    },
  });

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // ✅ read query params
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const onSubmit = (data: ISampleRequest) => {
    if (!requisitionId) {
      return;
    }
    setLoader(true);
    markExcmption(requisitionId, data.exception_reason).then((response) => {
      setLoader(false);
      const status = response?.status;
      if (status) {
        setOpen(false);
        processId &&
          navigate(
            `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
          );
      }
    });
  };
  const selectedExceptionReason = formHook.watch("exception_reason");

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={() => {
          setOpen(false);
          formHook.reset();
        }}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <form
              onSubmit={formHook.handleSubmit(onSubmit)}
              className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle w-full sm:max-w-xl"
            >
              {/* Premium Header */}
              <div className="bg-[#F9FAFB] px-6 py-4 flex items-center justify-between border-b border-gray-100 sticky top-0 z-10">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-black rounded-xl">
                    <Truck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 leading-tight">Reason for pending handover</h2>

                  </div>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded-full hover:bg-gray-200 transition-colors bg-gray-100"
                  onClick={() => {
                    formHook.reset();
                    setOpen(false);
                  }}
                >
                  <XMarkIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <div className="px-6 py-5 bg-white">
                <label htmlFor="exception_reason" className="block text-xs font-semibold text-gray-700 mb-2">
                  Select reason
                </label>
                <select
                  id="exception_reason"
                  {...formHook.register("exception_reason", { required: "Please select an exception reason" })}
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-500"
                >
                  <option value="">Select reason</option>
                  <option value="Pickup rider did not come">Pickup rider did not come </option>
                  <option value="Sample misplaced">Sample misplaced</option>
                  <option value="Sample turbid">Sample turbid</option>
                </select>
                {formHook.formState.errors.exception_reason?.message && (
                  <p className="text-xs text-red-600 mt-1">{formHook.formState.errors.exception_reason?.message}</p>
                )}
              </div>


              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                <button
                  type="button"
                  className="flex-1 px-4 py-2.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    formHook.reset();
                    setOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loader || !selectedExceptionReason}
                  className={clsx(
                    "flex-1 px-10 py-2.5 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-sm active:scale-95",
                    {
                      "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none": loader || !selectedExceptionReason,
                      "bg-black text-white hover:bg-gray-900": !loader && !!selectedExceptionReason,
                    },
                  )}
                >
                  {loader ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Requesting...</span>
                    </div>
                  ) : (
                    "Send to Lab"
                  )}
                </button>
              </div>
            </form>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default LabExemptionModal;
